import Highcharts from 'highcharts';
import type { ChartConfig, ChartPoint } from '../../types';
import type { ChartIndicatorsContext, ChartSeriesContext } from './types';

export const COLORS = {
  blue400: '#0D66D0',
  green400: '#33AB84',
  green150: '#E1EDE9',
  gray300: '#EAEAEA',
  gray350: '#D3D3D3',
  gray150: 'rgba(211, 211, 211, 0.18)',
  gray700: '#747474',
  gray800: '#505050',
  pink400: '#E663C9',
};

export const FONT_FAMILY = '"Open Sans", sans-serif';
export const WHITE_COLOR = '#FFFFFF';
export const FALLBACK_MARKER_COLOR = COLORS.blue400;
export const HOVER_TARGET_SERIES_NAME = 'hover-targets';
export const PLOT_BAND_WIDTH_PX = 15;
export const VISIBLE_MARKER_RADIUS_PX = 4;
export const VISIBLE_MARKER_BORDER_WIDTH_PX = 1;
export const HOVER_TARGET_RADIUS_PX = 5;
/** Halo discreto no hover para não poluir visualmente o gráfico. */
export const HOVER_BACKDROP_RADIUS_PX = 10;
export const HOVER_BACKDROP_FILL_OPACITY = 0.45;
export const HOVER_BACKDROP_STROKE_OPACITY = 0.7;
export const HOVER_BACKDROP_STROKE_WIDTH_PX = 4;
/** Marcador central no hover (acima do halo de fundo). */
export const HOVER_MARKER_RADIUS_PX = 4;
export const SERIES_LINE_WIDTH_PX = 1;
export const SPLINE_MARKER_RADIUS_PX = 5;
export const CHART_HEIGHT_PX = 420;
export const CHART_SPACING_TOP_PX = 20;
/**
 * Margens laterais do plot para rótulos dos eixos não serem cortados.
 * Em HML, o eixo Y estava exibindo apenas "%" por falta de espaço lateral.
 */
export const CHART_MARGIN_LEFT_PX = 44;
export const CHART_MARGIN_RIGHT_PX = 30;
/**
 * Padding relativo nas extremidades do eixo X — afasta o 1.º/último tick da borda útil
 * (homologação: 50%/110%/170% colados no container).
 */
export const X_AXIS_MIN_MAX_PADDING_RATIO = 0.035;
export const TOOLTIP_OFFSET_Y_PX = 18;
export const TOOLTIP_TOP_PADDING_RATIO = 0.1;
export const LEGEND_ITEM_GAP_PX = 4;
export const LEGEND_COMPETITOR_TRUNCATE_MAX_VIEWPORT_WIDTH = 1400;
export const X_AXIS_BREAK_START_OFFSET = 0.01;
export const X_AXIS_BREAK_END_OFFSET = 0.05;
export const X_AXIS_BREAK_SIZE = 0.02;
export const AXIS_VALUE_EPSILON = 1e-9;
export const INDICATOR_Z_INDEX = 6;
export const SPLINE_VOLUME_Z_INDEX = 2;
export const SPLINE_VALUE_Z_INDEX = 1;
export const BULLET_Z_INDEX = 9;
export const HIGHLIGHT_Z_INDEX = 10;
export const HOVER_TARGET_Z_INDEX = 11;
export const TRANSPARENT_COLOR = 'rgba(255,255,255,0)';
export const HOVER_INTERACTION_RADIUS_PX = 8;

export const DISABLED_HOVER_STATE = {
  hover: {
    enabled: false,
  },
} as const;

export const buildCircleMarker = (fillColor: string): Highcharts.PointMarkerOptionsObject => ({
  symbol: 'circle',
  radius: VISIBLE_MARKER_RADIUS_PX,
  fillColor,
  lineColor: WHITE_COLOR,
  lineWidth: VISIBLE_MARKER_BORDER_WIDTH_PX,
});

export const colorToRgba = (hexColor: string, alpha: number): string => {
  const hex = hexColor.replace('#', '');
  const red = parseInt(hex.slice(0, 2), 16);
  const green = parseInt(hex.slice(2, 4), 16);
  const blue = parseInt(hex.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

export const buildPeakHaloMarker = (fillColor: string): Highcharts.PointMarkerOptionsObject => ({
  symbol: 'circle',
  radius: HOVER_BACKDROP_RADIUS_PX,
  fillColor: colorToRgba(fillColor, HOVER_BACKDROP_FILL_OPACITY),
  lineColor: colorToRgba(fillColor, HOVER_BACKDROP_STROKE_OPACITY),
  lineWidth: 1,
});

export const formatPercent = (value?: number | null, digits = 1) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';

  return `${new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value)}%`;
};

export const formatCpi = (value?: number | null) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';

  return formatPercent(value * 100, 1);
};

export const hasBulletAtX = (points: Array<{ x: number }>, x: number) => {
  return points.some(point => Math.abs(point.x - x) < AXIS_VALUE_EPSILON);
};

export const isSameXAxisValue = (firstValue: number, secondValue: number) => {
  return Math.abs(firstValue - secondValue) < AXIS_VALUE_EPSILON;
};

/**
 * calcula o valor de y (no gráfico, o market share na escala percentual já transformada) para um CPI qualquer no eixo x.
 * quando o valor de vendas ou volume estiver selecionado, ajuda a calcular competitividade atual infopanel.
 */
export const interpolateYAtX = (sortedPoints: ChartPoint[], targetX: number): number | null => {
  if (!sortedPoints.length) return null;

  const exact = sortedPoints.find(pt => isSameXAxisValue(pt.x, targetX));
  if (exact) return exact.y;

  const first = sortedPoints[0];
  const last = sortedPoints[sortedPoints.length - 1];
  if (targetX <= first.x) return first.y;
  if (targetX >= last.x) return last.y;

  for (let i = 0; i < sortedPoints.length - 1; i++) {
    const x0 = sortedPoints[i].x;
    const x1 = sortedPoints[i + 1].x;
    if (targetX >= x0 && targetX <= x1) {
      const y0 = sortedPoints[i].y;
      const y1 = sortedPoints[i + 1].y;
      if (isSameXAxisValue(x0, x1)) return y0;
      const t = (targetX - x0) / (x1 - x0);
      return y0 + t * (y1 - y0);
    }
  }

  return null;
};

export const buildSeriesLegendIcon = (color: string) => `
<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M2 8H14" stroke="${color}" stroke-width="2" stroke-linecap="round" />
  <circle cx="8" cy="8" r="3" fill="${color}" stroke="white" stroke-width="1.5" />
</svg>
`;

export const buildIndicatorLegendIcon = (lineColor: string, bgColor: string) => `
<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect x="4" y="1" width="8" height="14" rx="4" fill="${bgColor}" />
  <path d="M8 2V14" stroke="${lineColor}" stroke-width="2" stroke-linecap="round" stroke-dasharray="1.5 2" />
  <circle cx="8" cy="8" r="2.5" fill="${lineColor}" stroke="white" stroke-width="1" />
</svg>
`;

export const buildCompetitorLegendIcon = (color: string) => `
<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M2 8H14" stroke="${color}" stroke-width="1.5" stroke-linecap="round" stroke-dasharray="3 2" />
</svg>
`;

export const buildPeakLegendIcon = (color: string) => `
<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
  <circle cx="8" cy="8" r="7" fill="${colorToRgba(color, HOVER_BACKDROP_FILL_OPACITY)}" stroke="${colorToRgba(
    color,
    HOVER_BACKDROP_STROKE_OPACITY,
  )}" stroke-width="1" />
  <circle cx="8" cy="8" r="4" fill="${color}" stroke="white" stroke-width="1.5" />
</svg>
`;

export const svgCompetitividade = buildIndicatorLegendIcon('#B0B0B0', '#F2F2F2');
export const svgOptimal = buildIndicatorLegendIcon('#33AB84', '#E7F6F0');
export const svgValue = buildSeriesLegendIcon(COLORS.pink400);
export const svgBlue = buildSeriesLegendIcon(COLORS.blue400);
export const svgValuePeak = buildPeakLegendIcon(COLORS.green400);

export const VALUE_PEAK_SERIES_NAME = 'Maior market share valor projetado';
export const VALUE_PEAK_HALO_SERIES_NAME = `${VALUE_PEAK_SERIES_NAME}-halo`;
export const VALUE_PEAK_LEGEND_INDEX = 10002;

export const getSeriesByMetric = (chartData: ChartConfig): ChartSeriesContext => {
  const volumeSeries = chartData.series.find(series => series.metric === 'VOLUME') ?? chartData.series[0];
  const valueSeries = chartData.series.find(series => series.metric === 'VALOR') ?? chartData.series[1];
  const volumeLineData = volumeSeries?.data ?? [];
  const valueLineData = valueSeries?.data ?? [];
  const shareVolumeData = volumeSeries?.bulletData?.length ? volumeSeries.bulletData : volumeLineData.filter(point => point.isBullet);
  const shareValueData = valueSeries?.bulletData?.length ? valueSeries.bulletData : valueLineData.filter(point => point.isBullet);

  return {
    volumeSeries,
    valueSeries,
    volumeLineData,
    valueLineData,
    shareVolumeData,
    shareValueData,
    competitorSeries: chartData.competitorSeries || [],
  };
};

export const getIndicatorsContext = (chartData: ChartConfig): ChartIndicatorsContext => {
  const cpiIndicator = chartData.indicators.find(indicator => indicator.key === 'cpiInfopanel' && indicator.show);
  const optimalIndicator = chartData.indicators.find(indicator => indicator.key === 'optimalCpi' && indicator.show);

  return {
    cpiIndicator,
    optimalIndicator,
    indicatorBandConfigs: [
      ...(cpiIndicator
        ? [
            {
              id: 'cpi-band' as const,
              lineId: 'cpi-line' as const,
              x: cpiIndicator.x,
              color: COLORS.gray150,
              lineColor: COLORS.gray350,
              dashArray: '4 4',
            },
          ]
        : []),
      // Faixa verde vertical (competitividade ótima) oculta temporariamente.
      // ...(optimalIndicator
      //   ? [
      //       {
      //         id: 'optimal-band' as const,
      //         lineId: 'optimal-line' as const,
      //         x: optimalIndicator.x,
      //         color: COLORS.green150,
      //         lineColor: COLORS.green400,
      //         dashArray: '2 3',
      //       },
      //     ]
      //   : []),
    ],
  };
};

export const clearPointHoverBackdrop = (point: Highcharts.Point) => {
  const pointWithBackdrop = point as Highcharts.Point & {
    hoverBackdrop?: Highcharts.SVGElement;
    graphic?: Highcharts.SVGElement;
  };
  pointWithBackdrop.hoverBackdrop?.destroy();
  pointWithBackdrop.hoverBackdrop = undefined;

  const markerOptions = (point.options as Highcharts.PointOptionsObject).marker;
  const seriesMarkerOptions = (point.series.options as Highcharts.SeriesScatterOptions).marker;
  const fallbackMarkerRadius =
    typeof markerOptions?.radius === 'number'
      ? markerOptions.radius
      : typeof seriesMarkerOptions?.radius === 'number'
        ? seriesMarkerOptions.radius
        : VISIBLE_MARKER_RADIUS_PX;
  const fallbackFillColor = (markerOptions?.fillColor ||
    seriesMarkerOptions?.fillColor ||
    point.color ||
    point.series.options.color ||
    FALLBACK_MARKER_COLOR) as string;
  const fallbackStrokeColor = (markerOptions?.lineColor ||
    seriesMarkerOptions?.lineColor ||
    markerOptions?.fillColor ||
    seriesMarkerOptions?.fillColor ||
    point.color ||
    point.series.options.color ||
    FALLBACK_MARKER_COLOR) as string;

  pointWithBackdrop.graphic?.attr({
    r: fallbackMarkerRadius,
    fill: fallbackFillColor,
    stroke: fallbackStrokeColor,
    'stroke-width': markerOptions?.lineWidth ?? seriesMarkerOptions?.lineWidth ?? 0,
  });
};

// Hover dos CPIs (bullets)
export const applyPointHoverBackdrop = (point: Highcharts.Point, color: string) => {
  const pointWithGraphic = point as Highcharts.Point & {
    graphic?: Highcharts.SVGElement;
    plotX?: number;
    plotY?: number;
    hoverBackdrop?: Highcharts.SVGElement;
  };

  if (!pointWithGraphic.graphic) return;

  clearPointHoverBackdrop(point);

  const chart = point.series.chart;
  const centerX = chart.plotLeft + (pointWithGraphic.plotX || 0);
  const centerY = chart.plotTop + (pointWithGraphic.plotY || 0);

  pointWithGraphic.hoverBackdrop = chart.renderer
    .circle(centerX, centerY, HOVER_BACKDROP_RADIUS_PX)
    .attr({
      fill: color,
      'fill-opacity': HOVER_BACKDROP_FILL_OPACITY,
      stroke: color,
      'stroke-opacity': HOVER_BACKDROP_STROKE_OPACITY,
      zIndex: 1,
    })
    .add();

  pointWithGraphic.graphic.attr({
    r: HOVER_MARKER_RADIUS_PX,
    fill: color,
    stroke: WHITE_COLOR,
    'stroke-width': VISIBLE_MARKER_BORDER_WIDTH_PX,
    zIndex: BULLET_Z_INDEX,
  });

  pointWithGraphic.graphic.toFront();
};

// Hover dos competidores (bullets)
export const applyHoverBackdropAt = (chart: Highcharts.Chart, centerX: number, centerY: number, color: string) => {
  const backdrop = chart.renderer
    .circle(centerX, centerY, HOVER_BACKDROP_RADIUS_PX)
    .attr({
      fill: color,
      'fill-opacity': HOVER_BACKDROP_FILL_OPACITY,
      stroke: color,
      'stroke-opacity': HOVER_BACKDROP_STROKE_OPACITY,
      zIndex: BULLET_Z_INDEX - 1,
    })
    .add();

  const marker = chart.renderer
    .circle(centerX, centerY, HOVER_MARKER_RADIUS_PX)
    .attr({
      fill: color,
      stroke: WHITE_COLOR,
      'stroke-width': VISIBLE_MARKER_BORDER_WIDTH_PX,
      zIndex: BULLET_Z_INDEX,
    })
    .add();

  marker.toFront();

  return { backdrop, marker };
};

export const organizeLegendWithBottomRow = (chart: Highcharts.Chart): void => {
  const legend = chart.legend as unknown as {
    allItems?: Array<
      Highcharts.Series & {
        _legendItemPos?: [number, number];
        legendItem?: unknown;
        options?: { custom?: { legendGroup?: 'top' | 'bottom' } };
      }
    >;
    itemDistance?: number;
    itemHeight?: number;
    positionItem?: (item: unknown) => void;
  } | null;

  if (!legend?.allItems?.length) return;

  const hasCompetitors = chart.series.some(s => (s.options as { custom?: { isCompetitor?: boolean } } | undefined)?.custom?.isCompetitor);
  if (!hasCompetitors) return;

  const bottomItems = legend.allItems.filter(item => item.options?.custom?.legendGroup === 'bottom');
  if (!bottomItems.length) return;

  const topItems = legend.allItems.filter(item => item.options?.custom?.legendGroup !== 'bottom');
  if (!topItems.length) return;

  const topRowMaxY = Math.max(...topItems.map(it => it._legendItemPos?.[1] ?? 0));
  const rowHeight = typeof legend.itemHeight === 'number' ? legend.itemHeight : 18;
  const rowGap = 8;
  const bottomRowY = topRowMaxY + rowHeight + rowGap;

  const itemDistance = typeof legend.itemDistance === 'number' ? legend.itemDistance : 16;
  let cursorX = 0;

  const getLegendItemWidth = (item: (typeof legend.allItems)[number]): number => {
    const li = item.legendItem as { getBBox?: () => { width: number }; label?: { getBBox?: () => { width: number } } } | undefined;
    return li?.getBBox?.().width ?? li?.label?.getBBox?.().width ?? 0;
  };

  bottomItems.forEach(item => {
    if (!item._legendItemPos) return;
    item._legendItemPos[0] = cursorX;
    item._legendItemPos[1] = bottomRowY;
    legend.positionItem?.(item);

    cursorX += getLegendItemWidth(item) + itemDistance;
  });
};
